import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { InventoryEntryDetailModel } from 'src/app/models/inventory/inventory-entry-detail.model';
import { InventoryEntryModel } from 'src/app/models/inventory/inventory-entry.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { SubrentalEntryDetailService } from 'src/app/services/subrental/subrental-entry-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-subrental-entry-detail',
  templateUrl: './register-subrental-entry-detail.component.html',
  styleUrls: ['./register-subrental-entry-detail.component.scss'],
  standalone: false
})
export class RegisterSubrentalEntryDetailComponent implements OnInit, OnDestroy {
  @ViewChild('articleSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  @Input() disabled: boolean = false;
  @Input('subrentalEntryContract')
  set changeSubrental(changeSubrental: InventoryEntryModel) {
    this.changeSubrentalEntry(changeSubrental);
  }
  @Input('subrentalEntryDetailContract')
  set changeSubrentalDetail(changeSubrentalDetail: InventoryEntryDetailModel) {
    this.changeSubrentalEntryDetail(changeSubrentalDetail);
  }
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  ascendantArticle: boolean = true;
  columnSortArticle: string | null = null;
  dataArticle: PagedResponseModel<any> = new PagedResponseModel<any>();
  filtersArticle: ColumnResponseModel[] = []
  formSubrentalEntryDetail: FormGroup = new FormGroup({});
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: [],
  })
  subrentalEntryDetailId: number = 0;
  subrentalEntryContract: InventoryEntryModel = new InventoryEntryModel();
  subrentalEntryDetailContract: InventoryEntryDetailModel = new InventoryEntryDetailModel();
  limitArticle: number = 5;
  listArticles: any[] = [];
  listStores: any[] = [];
  output$ = new Subject<any>();
  pageArticle: number = 1;
  storeId: number = 0;
  subscription: Subscription;
  componentTableSelection = CustomTableSelectComponent;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private subrentalEntryDetailService: SubrentalEntryDetailService,
    private storeService: StoreService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new InventoryEntryDetailModel);
  }

  async ngOnInit() {
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticle(payload);
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
    await this.getListArticlesForSelect();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  calculateValues(): void {
    const form = this.formSubrentalEntryDetail;
    const quantity = form.get("quantity")?.value ?? 0;
    const unitValue = form.get("unitValue")?.value ?? 0;
    const totalValue = quantity * unitValue;
    form.patchValue({ totalValue });
  }

  async changeSubrentalEntry(changeSubrental: InventoryEntryModel) {
    if (changeSubrental.branchId != this.subrentalEntryContract.branchId) {
      this.getListStoresForSelect(changeSubrental.branchId, changeSubrental.inventoryId);
    }
    if (changeSubrental.inventoryEntryId != 0) {
      this.subrentalEntryContract = changeSubrental;
    }
  }

  async changeSubrentalEntryDetail(changeSubrentalDetail: InventoryEntryDetailModel) {
    this.subrentalEntryDetailContract = changeSubrentalDetail;
    this.storeId = 0;
    this.articleId = 0;

    this.validateForm(changeSubrentalDetail);

    if (changeSubrentalDetail.inventoryEntryDetailId !== 0) {
      this.subrentalEntryDetailId = changeSubrentalDetail.inventoryEntryDetailId;
      await Promise.all([
        this.getStore(changeSubrentalDetail.storeId, false),
        this.getArticle(changeSubrentalDetail.articleId, false)
      ]);
      await this.getStocksByArticleIdStoreId();
    }
  }

  clearForm() {
    this.storeId = 0;
    this.articleId = 0;
    const modelSubrentalEntryDetail = new InventoryEntryDetailModel;
    this.validateForm(modelSubrentalEntryDetail)
    this.isFormView.emit(false);
  }

  async getArticle(articleId: number | null, reload: boolean) {
    let validArticle = null;
    if (articleId != null) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        validArticle = articleId;
      }
      if (!article.active) {
        this.toastHandlerService.showToast(
          'warning',
          'El artículo seleccionado se encuentra inactivo.',
        );
      }
    }

    this.articleId = validArticle ?? 0;
    this.formSubrentalEntryDetail.controls["articleId"].patchValue(validArticle);
    if (reload) {
      await this.getSubrentalEntryDetailByKeys();
    }
  }

  async getArticlesForEntries() {
    try {
      this.dataArticle = await firstValueFrom(this.subrentalEntryDetailService.getArticlesForEntries(this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos', error);
    }
  }

  async getColumnsArticle() {
    try {

      this.headerArticle = await firstValueFrom(this.subrentalEntryDetailService.getColumnsForArticles());
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getListArticlesForSelect() {
    try {
      this.listArticles = await firstValueFrom(this.subrentalEntryDetailService.getAllArticlesForSubrentalEntriesForSelectAsync());
    } catch (error) {
      console.error('Error al obtener la lista de artículos.', error);
    }
  }

  async getListStoresForSelect(branchId: number, inventoryId: number) {
    try {
      this.listStores = await firstValueFrom(this.storeService.getStoresActivesByBranchInventoryForSelect(branchId, inventoryId));
    } catch (error) {
      console.error('Error al obtener la lista de bodegas.', error);
    }
  }

  async getStocksByArticleIdStoreId() {
    if (this.storeId != 0 && this.articleId != 0) {
      const rentalStockModel = new RentalStockModel();
      rentalStockModel.articleId = this.articleId;
      rentalStockModel.storeId = this.storeId;
      rentalStockModel.dateMovement = this.subrentalEntryContract.dateEntry;

      const stock = await firstValueFrom(this.subrentalEntryDetailService.getStocksByArticleIdStoreId(rentalStockModel));
      this.formSubrentalEntryDetail.controls["stock"].patchValue(stock);
    }
  }

  async getStore(storeId: number | null, reload: boolean) {
    this.storeId = storeId ?? 0;
    this.formSubrentalEntryDetail.controls["storeId"].patchValue(storeId);

    if (reload) {
      this.subrentalEntryDetailId = 0;
      const modelSubrentalEntryDetail = new InventoryEntryDetailModel;
      modelSubrentalEntryDetail.articleId = this.articleId;
      modelSubrentalEntryDetail.storeId = this.storeId;
      this.validateForm(modelSubrentalEntryDetail);
      await this.getSubrentalEntryDetailByKeys();
    }
  }

  async getSubrentalEntryDetailByKeys() {
    if (this.storeId === 0 || this.articleId === 0) return;

    const entryId = this.subrentalEntryContract.inventoryEntryId;
    const response = await firstValueFrom(
      this.subrentalEntryDetailService.getSubrentalEntryDetailByKeys(entryId, this.storeId, this.articleId));
    if (response.inventoryEntryDetailId === 0) {
      this.subrentalEntryDetailContract = new InventoryEntryDetailModel();
      this.subrentalEntryDetailId = 0;
      this.subrentalEntryDetailContract.storeId = this.storeId;
      this.subrentalEntryDetailContract.articleId = this.articleId;
      this.subrentalEntryDetailContract.unitValue = response.unitValue;

      this.validateForm(this.subrentalEntryDetailContract);
      await this.getStocksByArticleIdStoreId();
      return;
    }

    this.subrentalEntryDetailContract = response;
    this.subrentalEntryDetailId = response.inventoryEntryDetailId;
    this.validateForm(response);
    await Promise.all([
      this.getStore(response.storeId, false),
      this.getArticle(response.articleId, false)
    ]);

    await this.getStocksByArticleIdStoreId();
  }

  async handleSelectedItem(selectedItem: any) {
    const articleId = parseInt(selectedItem[0]?.id);
    this.getArticle(articleId, true)
  }

  async onPageChangeArticle(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    this.getArticlesForEntries();
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async openArticleSelectionModal() {
    this.filtersArticle = [];
    await Promise.all([
      this.getColumnsArticle(),
      this.getArticlesForEntries()]
    );
    this.updateInputs();
    this.articleSelectionModalComponent && this.articleSelectionModalComponent.show();
  }

  async send(): Promise<any> {
    if (this.formSubrentalEntryDetail.valid) {

      const modelSubrentalEntryDetail = this.setForm();
      if (modelSubrentalEntryDetail.totalValue < 0) {
        this.toastHandlerService.showToast('warning', 'El valor total no puede ser negativo.', 'Atención');
        return;
      }

      try {
        if (this.subrentalEntryDetailId == 0) {
          await firstValueFrom(this.subrentalEntryDetailService.createSubrentalEntryDetail(modelSubrentalEntryDetail));
          this.toastHandlerService.showToast('success', 'El detalle fue creado exitosamente.', '');
        }
        else {
          await firstValueFrom(this.subrentalEntryDetailService.updateSubrentalEntryDetail(modelSubrentalEntryDetail));
          this.toastHandlerService.showToast('success', 'El detalle fue modficado exitosamente.', '');
        }

        this.subrentalEntryDetailContract = new InventoryEntryDetailModel();
        this.clearForm();
        this.isFormView.emit(true);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al guardar el detalle.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  setForm(): any {
    this.subrentalEntryDetailContract.storeId = this.formSubrentalEntryDetail.get("storeId")?.value;
    this.subrentalEntryDetailContract.articleId = this.formSubrentalEntryDetail.get("articleId")?.value;
    this.subrentalEntryDetailContract.quantity = this.formSubrentalEntryDetail.get("quantity")?.value;
    this.subrentalEntryDetailContract.unitValue = this.formSubrentalEntryDetail.get("unitValue")?.value;
    this.subrentalEntryDetailContract.totalValue = this.formSubrentalEntryDetail.get("totalValue")?.value;
    this.subrentalEntryDetailContract.inventoryEntryId = this.subrentalEntryContract.inventoryEntryId;

    return this.subrentalEntryDetailContract;
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      storeId: {
        required: 'El campo bodega es obligatorio.'
      },
      articleId: {
        required: 'El campo artículo es obligatorio.'
      },
      quantity: {
        required: 'El campo cantidad es obligatorio.',
        min: 'La cantidad mínima es 1.'
      },
      unitValue: {
        min: 'El valor unitario mínimo es 0.',
        required: 'El campo valor unitario es obligatorio.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formSubrentalEntryDetail.get(controlName);

      if (control && control.invalid && control.errors) {
        const errors = control.errors;

        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: false,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }

  validateForm(subrentalEntryDetail: InventoryEntryDetailModel) {
    this.formSubrentalEntryDetail = this.formBuilder.group({
      storeId: [subrentalEntryDetail.storeId, [Validators.required, Validators.min(1)]],
      articleId: [subrentalEntryDetail.articleId, [Validators.required, Validators.min(1)]],
      stock: [0, []],
      quantity: [subrentalEntryDetail.quantity, [Validators.required, Validators.min(1)]],
      unitValue: [subrentalEntryDetail.unitValue, [Validators.required, Validators.min(0)]],
      totalValue: [subrentalEntryDetail.quantity * subrentalEntryDetail.unitValue, []]
    });
    this.formControlStateService.setControlState(this.formSubrentalEntryDetail, ['totalValue'], false);

  }
}
